import { useState, ReactNode } from "react";
import { Helmet } from "react-helmet";
import { useParams, useLocation, useHistory } from "react-router";
import { gql, useQuery, useMutation } from "@apollo/client";
import { format } from "date-fns";

import { useStoreContext } from "../../contexts/StoreProvider";
import Layout from "../../components/Layout";
import Profile from "../../components/Client/Profile";
import History from "../../components/Client/History";
import TabNavigation from "../../components/TabNavigation";
import { Text, Heading } from "../../components/Type";
import userPlaceholder from "../../assets/user-placeholder.svg";
import { ArrowRight } from "../../components/Icons/general-interface-1";
import styles from "./index.module.scss";
import TextLink from "../../components/TextLinks";
import StatusPill from "../../components/StatusPills";
import StatusPillDropdown from "../../components/StatusPillsDropdown";
import Button from "../../components/Button";
import Loader from "../../components/Loader";
import { ClientFileType } from "../../types/clientFileType";
import NotificationBanner from "../../components/NotificationBanner";
import UploadXmlModal from "./UploadXmlModal";
import { findStatus, postSigningStatusList } from "../../utils/status";
import { isAdmin, isAuthorizer, isSalesRep } from "../../utils/user";
import { FileStatusType } from "../../types/fileStatusType";
import Modal from "../../components/Modal";
import ModalActions from "../../components/ModalActions";
import ViewOnlyModal from "../../components/ViewOnlyModal";

const GET_CLIENT = gql`
  query Client($id: ID!) {
    client(id: $id) {
      id
      first_name
      last_name
      social_assistance
      health_card
      health_card_version
      birth_date
      long_term_care_home
      sex
      cep
      address {
        address_1
        address_2
        unit_number
        street_number
        street_name
        lot
        business_phone
        business_phone_ext
        postal_code
        city
        province
        phone
      }
      agent {
        unit_number
        street_number
        street_name
        lot
        business_phone
        business_phone_ext
        postal_code
        city
        province
        phone
        first_name
        last_name
        middle_initial
        relationship
      }
      authorizers {
        id
        first_name
        last_name
        role {
          type
          name
        }
      }
      sales_reps {
        id
        first_name
        last_name
        role {
          type
          name
        }
      }
      client_files {
        id
        status
        locked_by {
          id
          first_name
          last_name
          role {
            type
            name
          }
        }
        quote {
          url
        }
        client_form {
          id
          updated_at
        }
        file_events {
          id
          created_at
          message
        }
        ds_envelope_id
        ds_signed_form {
          name
        }
        ds_signed_form_original {
          name
        }
      }
    }
  }
`;

const UPDATE_STATUS = gql`
  mutation UpdateClientFile($input: updateClientFileInput) {
    updateClientFile(input: $input) {
      clientFile {
        updated_at
        status
      }
    }
  }
`;

const ClientPage = () => {
  const { state: { user }, } = useStoreContext();
  const params = useParams<{ client_id: string }>();
  const location = useLocation();
  const history = useHistory();
  const { data, loading, error } = useQuery(GET_CLIENT, {
    fetchPolicy: "network-only",
    variables: {
      id: params.client_id,
    },
    skip: !params || !params.client_id,
  });

  const currentFile: ClientFileType = data?.client?.client_files?.find(
    (file) => file.status !== "Archived"
  );

  const [isUploadXmlModalOpen, setUploadXmlModalOpen] = useState(false);
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);

  const [, setBoundaryError] = useState<Error | null>(null);
  const [pendingFileStatusUpdate, setPendingFileStatusUpdate] = useState<FileStatusType | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const [updateStatus] = useMutation(UPDATE_STATUS, {
    refetchQueries: [GET_CLIENT],
    awaitRefetchQueries: true,
  });

  const handleStatusChange = (status: FileStatusType) => {
    if (status !== currentFile?.status) {
      setPendingFileStatusUpdate(status);
    }
  };

  const updateFileStatus = () => {
    if (!user) throw new Error("No user");
    setUpdatingStatus(true);
    updateStatus({
      variables: {
        input: {
          where: {
            id: currentFile.id,
          },
          data: {
            status: pendingFileStatusUpdate,
            last_updated_by: user.id,
          },
        },
      },
      refetchQueries: [GET_CLIENT],
      awaitRefetchQueries: true,
    })
      .then(() => {
        setPendingFileStatusUpdate(null);
        setUpdatingStatus(false);
      })
      .catch((error) => {
        setBoundaryError(() => {
          throw error;
        });
      });
  };

  const isPostSigning = postSigningStatusList.some(
    ({ value }) => value === currentFile?.status
  );
  const viewOnly = (currentFile?.locked_by && currentFile?.locked_by.id !== user?.id) && !isAdmin(user);
  const canChangeStatus = (isAdmin(user) || isSalesRep(user)) && !viewOnly;
  const canChangeStatusToEditable =
    isPostSigning ||
    currentFile?.status === "PendingSignature" ||
    currentFile?.status === "Cancelled";
  const editableStatuses = [findStatus("Progress"), findStatus("Review")];
  const statusOptions = isPostSigning
    ? [...postSigningStatusList, findStatus("Cancelled")]
    : [findStatus("Cancelled")];
  if (!statusOptions.some(({ value }) => value === currentFile?.status)) {
    statusOptions.unshift(findStatus(currentFile?.status));
  }
  if (canChangeStatusToEditable) {
    if (currentFile?.status === "Cancelled") {
      statusOptions.unshift(findStatus("Review"));
      statusOptions.unshift(findStatus("Progress"));
    } else {
      statusOptions.unshift(...editableStatuses);
    }
  }
  const statusChangeWarnings: { warning: string | ReactNode; key?: string }[] =
    [];
  if (
    pendingFileStatusUpdate === "Cancelled" &&
    currentFile?.status === "PendingSignature" &&
    currentFile?.ds_envelope_id
  ) {
    statusChangeWarnings.push({
      warning:
        "Signing was initialized, but not completed. The signing process will be cancelled, and new signatures will need to be collected.",
    });
  }
  if (editableStatuses.some(({ value }) => value === pendingFileStatusUpdate)) {
    statusChangeWarnings.push({
      warning: "This change will make the application editable again.",
    });
    if (
      currentFile?.status === "PendingSignature" &&
      currentFile?.ds_envelope_id
    ) {
      statusChangeWarnings.push({
        warning:
          "Signing was initialized, but not completed. The signing process will be cancelled, and new signatures will need to be collected",
      });
    }
    if (currentFile?.status !== "PendingSignature") {
      if (currentFile?.ds_signed_form_original?.name) {
        statusChangeWarnings.push({
          warning: (
            <>
              This file already contains an original signed application PDF, so
              it will be retained and the most recent signed PDF application
              will be <b>deleted.</b>
            </>
          ),
          key: "willBeDeleted",
        });
      } else {
        statusChangeWarnings.push({
          warning:
            "The existing DocuSign PDF will be retained on the file as the original signed application.",
        });
      }
    }
    if (currentFile?.ds_signed_form?.name) {
      statusChangeWarnings.push({
        warning:
          "After editing is finished, the file will need to be signed again by both the authorizer and vendor.",
      });
    }
  }
  const getFormAction = () => {
    // ToDo: if/when this part of the page is wrapped in a ClientFileProvider we can determine this from formMode instead
//    if (isLocked) return "View";
    if (isAuthorizer(user) && currentFile.status === "NotStarted") return "Start";
    if (isAuthorizer(user) && !viewOnly && ["Progress", "Review", "Action"].includes(currentFile.status)) return "Edit";
    if (isSalesRep(user) && ["Progress", "Review", "Action"].includes(currentFile.status)) return "Review";
    return "View";
  };

  const canImportXML = () => {
    return (isSalesRep(user) || isAuthorizer(user)) &&
           (!currentFile.locked_by || (currentFile.locked_by && currentFile.locked_by.id === user?.id)) &&
           ["NotStarted", "Progress", "Action"].includes(currentFile.status);
  };

  return (
    <>
      <Layout>
        <Helmet>
          <title>Client</title>
        </Helmet>
        <div className={styles.container}>
          {error && error.message}
          {loading ? (
            <div className={styles.loader}>
              <Loader heading="Fetching client..." />
            </div>
          ) : (
            <>
              {data && data.client ? (
                <>
                  <div className={styles.content}>
                    <div className={styles.profileHeader}>
                      <div className={styles.profileImage}>
                        <img
                          src={userPlaceholder}
                          alt={`${data.client.first_name} ${data.client.last_name}`}
                        />
                      </div>
                      <div>
                        <div className={styles.profileContent}>
                          <div className={styles.profileContentWrap}>
                            <Heading
                              size="h3"
                              className={styles.profileName}
                            >{`${data.client.first_name} ${data.client.last_name}`}</Heading>
                            <Text
                              size="small"
                              lineHeight="medium"
                              className={styles.profileNum}
                            >
                              {data.client.health_card}
                            </Text>
                          </div>
                          <div className={styles.profileAction}>
                            <TextLink
                              to={`/client/${data.client.id}/edit`}
                              className={styles.profileLink}
                            >
                              Edit Profile
                            </TextLink>
                          </div>
                        </div>
                      </div>
                    </div>
                    {currentFile && (
                      <div className={styles.status}>
                        {currentFile.client_form !== null && (
                          <>
                          <Text
                          size="small"
                          className={styles.statusDate}
                        >{`Last updated ${format(
                          new Date(currentFile.client_form.updated_at),
                          "MM/dd/yyyy hh:mm a"
                        )}`}</Text>
                        {canChangeStatus ? (
                          <StatusPillDropdown
                            status={ pendingFileStatusUpdate || currentFile.status }
                            onStatusChange={(status) =>
                              handleStatusChange(status)
                            }
                            options={statusOptions}
                          />
                        ) : (
                          <StatusPill status={currentFile.status} />
                        )}
                        </>
                        )}

                        <div>
                        <Button
                          secondary
                          className={styles.statusButton}
                          onClick={() => setUploadXmlModalOpen(true)}
                          disabled={!canImportXML()}
                        >
                          Import XML
                        </Button>
                        <Button
                          primary
                          icon={<ArrowRight />}
                          className={styles.statusButton}
                          onClick={() =>
                            history.push(
                              `/client/${params.client_id}/form/${currentFile.id}`
                            )
                          }
                        >
                          {`${getFormAction()} ADP Form`}
                        </Button>
                        </div>
                      </div>
                    )}

                    <div className={styles.nav}>
                      <TabNavigation
                        links={[
                          {
                            url: isAdmin(user)
                              ? `/admin/client/${params.client_id}`
                              : `/client/${params.client_id}`,
                            text: "Client Overview",
                          },
                          {
                            url: isAdmin(user)
                              ? `/admin/client/${params.client_id}#history`
                              : `/client/${params.client_id}#history`,
                            text: "Application History",
                          },
                        ]}
                      />
                    </div>
                  </div>
                  {location.hash === "#history" ? (
                    <History client_files={data.client.client_files} />
                  ) : (
                    <Profile
                      client={data.client}
                      file={currentFile}
                      formAction={
                        data?.client.client_files[0].status || undefined
                      }
                    />
                  )}
                </>
              ) : (
                <div className={styles.error}>
                  <NotificationBanner
                    error
                    title="Uh Oh!"
                    content="There was a problem fetching this clients profile, ensure you have the proper access to this data."
                  />
                </div>
              )}
            </>
          )}
        </div>

        <Modal
          className={styles.modalUpload}
          title="XML File Upload"
          isOpen={isUploadXmlModalOpen}
          onClose={() => { setUploadXmlModalOpen(false); }}
          closeOnOverlay={false}
        >
          <UploadXmlModal
            fileStatus={currentFile?.status}
            fileId={currentFile?.id}
            setUploadXmlModalOpen={setUploadXmlModalOpen}
            setSuccessModalOpen={setSuccessModalOpen}
            client={data?.client}
            formId={currentFile?.client_form?.id}
          />
        </Modal>

        <Modal
          isOpen={isSuccessModalOpen}
          title="Import Success"
          onClose={() => { setSuccessModalOpen(false); }}
        >
          <div className={styles.content}>
            <Text size="large">
              A draft of the ADP form has been created.
            </Text>
            <br />
            <Text>
              { isAuthorizer(user)
                ? "You can now continue editing the application or submit it for review."
                : "The clinical care team has been notified."
              }
            </Text>
          </div>
          <ModalActions>
            <Button
              primary
              onClick={() => { setSuccessModalOpen(false); }}
            >
              Done
            </Button>
          </ModalActions>
        </Modal>

      </Layout>
      <Modal
        title="Confirm status change"
        isOpen={!!pendingFileStatusUpdate}
        onClose={() => {
          if (!updatingStatus) setPendingFileStatusUpdate(null);
        }}
      >
        {pendingFileStatusUpdate && (
          <>
            <Text>
              The status of this file will be changed to{" "}
              <b>‘{findStatus(pendingFileStatusUpdate).label}’</b>, and a
              notification will be sent to each care team member.
            </Text>
            {statusChangeWarnings.length > 0 && (
              <div className={styles.pleaseNote}>
                <Text weight="bold">Please note:</Text>
                <ul>
                  {statusChangeWarnings.map(({ warning, key }) => (
                    <Text
                      component="li"
                      key={typeof warning === "string" ? warning : key}
                    >
                      {warning}
                    </Text>
                  ))}
                </ul>
              </div>
            )}
            <ModalActions>
              <Button
                onClick={updateFileStatus}
                disabled={updatingStatus}
                primary
              >
                {updatingStatus ? "Updating..." : "Update"}
              </Button>
              <TextLink
                onClick={() => {
                  if (!updatingStatus) setPendingFileStatusUpdate(null);
                }}
              >
                Cancel
              </TextLink>
            </ModalActions>
          </>
        )}
      </Modal>
      {(currentFile && viewOnly) && (
        <ViewOnlyModal
          lockName={`${currentFile?.locked_by?.first_name} ${currentFile?.locked_by?.last_name}`}
          lockRole={currentFile?.locked_by?.role.name}
          userRole={user?.role?.name}
          isSubmitted={currentFile.status === "PendingSignature"}
        />
      )}
    </>
  );
};

export default ClientPage;
